import {SsaiService} from '../../../core/ssai/SsaiService';
import {isFiniteNumber} from '../../../utils/numberUtils';
import {calculateTimeWithUndefined, isNotNullish, isNullish} from '../../../utils/Utils';
import {
  AdCallbacks,
  AdErrorPayload,
  AdManifestLoadedPayload,
  AdModuleAPI,
  AdQuartile,
} from '../../internal/ads/AdModuleAPI';
import type {THEOplayerProxy} from '../THEOplayerProxy';

import {isAdErrorEvent, isSsaiIntegration, mapTheoAd, mapTheoAdBreak, toSsaiAdPosition} from './AdUtils';

export class THEOplayerAdModule implements AdModuleAPI {
  adCallbacks = new AdCallbacks();
  /**
   * this field stores the duration of the video.
   * during active ad the property `player.duration` shows the duration of the ad.
   */
  private videoDuration: number | undefined;

  /**
   * Routes the current ad break to the SSAI pipeline instead of the CSAI `adCallbacks`.
   * Decided once at `adbreakbegin` from the ad break integration and kept for the whole break.
   */
  private currentBreakIsSsai = false;

  /**
   * Set when the current ssai ad is skipped, so the ad-end event is not reported as a
   * completed quartile.
   */
  private currentSsaiAdEndedPrematurely = false;

  private readonly player: THEOplayerProxy;
  private readonly ssaiService: SsaiService;

  constructor(player: THEOplayerProxy, ssaiService: SsaiService) {
    this.player = player;
    this.ssaiService = ssaiService;
  }

  isLinearAdActive = (): boolean => {
    const ads = this.player.ads;
    if (isNullish(ads)) {
      return false;
    }
    return ads.playing ?? false;
  };

  getContainer = (): HTMLElement | undefined => this.player.element;

  getAdModuleInfo = (): {name?: string; version?: string} => {
    return {name: 'theo-ads', version: undefined};
  };

  currentTime = (): number => this.player.currentTime ?? 0;

  getAutoPlay = (): boolean => this.player.autoplay ?? false;

  registerEventListeners(): void {
    const ads = this.player.ads;
    if (isNullish(ads)) {
      return;
    }

    // ------------------------------------------------------------------------------------------------------
    // !! Note that when dealing with client-side advertisements, you have different types of integrations !!
    // The impact of this is that some events are not available throughout all integrations.
    // ------------------------------------------------------------------------------------------------------
    // See: https://github.com/THEOplayer/documentation/blob/c1a8a66672a80ba5918a5d014329b2ad93dad6ec/theoplayer/how-to-guides/01-ads/11-how-to-subscribe-to-ad-events.md#web-sdk

    ads.addEventListener('adbreakbegin', (event) => {
      // decision for the whole break.
      this.currentBreakIsSsai = isSsaiIntegration(event.adBreak.integration);
      const adBreak = mapTheoAdBreak(event.adBreak, this.videoDuration);
      if (this.currentBreakIsSsai) {
        this.currentSsaiAdEndedPrematurely = false;
        // Limitation: `expectedPaidAds` and `expectedSlates` are intentionally omitted. The player ad
        // events do not expose how many paid ads / slates an SSAI ad break is expected to contain, so
        // these counts cannot be derived from automatic tracking (unlike the manual SSAI API, where the
        // customer provides them).
        this.ssaiService.adBreakStart({adPosition: toSsaiAdPosition(adBreak?.position)});
      } else {
        this.adCallbacks.onAdBreakStarted(adBreak);
      }
    });
    ads.addEventListener('adbreakend', (event) => {
      if (this.currentBreakIsSsai) {
        this.ssaiService.adBreakEnd();
      } else {
        this.adCallbacks.onAdBreakFinished(mapTheoAdBreak(event.adBreak, this.videoDuration));
      }
      this.currentBreakIsSsai = false;
    });
    // needed to track ad abandonment due to source unload event
    ads.addEventListener('removeadbreak', (_event) => {
      if (this.currentBreakIsSsai) {
        this.ssaiService.unload();
      } else {
        this.adCallbacks.onBeforeUnload();
      }
    });
    ads.addEventListener('adbegin', (event) => {
      const theoAd = mapTheoAd(event.ad);
      if (this.currentBreakIsSsai) {
        this.currentSsaiAdEndedPrematurely = false;
        const adMetadata = {
          adId: theoAd?.id,
          adSystem: theoAd.vastData?.adSystem,
          durationInMs: calculateTimeWithUndefined(theoAd?.durationInSec),
          isSlate: theoAd?.isSlate,
          universalAdIdValue: theoAd?.vastData?.universalAdIdValue,
          universalAdIdRegistry: theoAd?.vastData?.universalAdIdRegistry,
          creativeId: theoAd?.vastData?.creativeId,
          creativeAdId: theoAd?.vastData?.creativeAdId,
          advertiserName: theoAd?.vastData?.advertiserName,
          adTitle: theoAd?.vastData?.adTitle,
        };
        this.ssaiService.adStart(adMetadata);
      } else {
        this.adCallbacks.onAdStarted(theoAd);
      }
    });
    ads.addEventListener('adend', (event) => {
      if (this.currentBreakIsSsai) {
        // The ad-end event also fires for ads that were skipped or failed; those must not be
        // reported as completed.
        if (this.currentSsaiAdEndedPrematurely) {
          return;
        }
        this.ssaiService.adQuartileFinished('completed', {});
      } else {
        this.adCallbacks.onAdFinished(mapTheoAd(event.ad));
      }
    });
    ads.addEventListener('adskip', (event) => {
      if (this.currentBreakIsSsai) {
        // We do not track skipping for ssai
        this.currentSsaiAdEndedPrematurely = true;
      } else {
        this.adCallbacks.onAdSkipped(mapTheoAd(event.ad));
      }
    });
    ads.addEventListener('aderror', (event) => {
      const errorPayload: AdErrorPayload = {};

      if (isAdErrorEvent(event)) {
        errorPayload.code = event.errorCode;
        errorPayload.message = event.message;
      }

      // fallback to player recorded error if event does not provide error info
      const errorObject = this.player.errorObject;
      if (isNotNullish(errorObject) && isNullish(errorPayload.code)) {
        errorPayload.code = errorObject.code;
        errorPayload.message = errorObject.message;
      }

      if (this.currentBreakIsSsai) {
        // TODO: Will be implemented with https://bitmovin.atlassian.net/browse/AN-5588
      } else {
        this.adCallbacks.onAdError(errorPayload);
      }
    });
    ads.addEventListener('adfirstquartile', (_event) => {
      if (this.currentBreakIsSsai) {
        this.ssaiService.adQuartileFinished('first', {});
      } else {
        this.adCallbacks.onAdQuartile({quartile: AdQuartile.FIRST_QUARTILE});
      }
    });
    ads.addEventListener('admidpoint', (_event) => {
      if (this.currentBreakIsSsai) {
        this.ssaiService.adQuartileFinished('midpoint', {});
      } else {
        this.adCallbacks.onAdQuartile({quartile: AdQuartile.MIDPOINT});
      }
    });
    ads.addEventListener('adthirdquartile', (_event) => {
      if (this.currentBreakIsSsai) {
        this.ssaiService.adQuartileFinished('third', {});
      } else {
        this.adCallbacks.onAdQuartile({quartile: AdQuartile.THIRD_QUARTILE});
      }
    });
    ads.addEventListener('adloaded', (_event) => {
      const playerDuration = this.player.duration;
      if (isFiniteNumber(playerDuration) && this.videoDuration !== playerDuration) {
        this.videoDuration = playerDuration;
      }

      // Ad manifest tracking is only part of the CSAI pipeline. The video duration update above is kept for SSAI
      // ad break position detection.
      if (this.currentBreakIsSsai) {
        return;
      }

      const adManifestPayload: AdManifestLoadedPayload = {
        isVmap: false,
      };

      const sourceAds = this.player.source?.ads;
      if (isNotNullish(sourceAds) && sourceAds.length > 0) {
        // if VMAP is set up only one ad will be defined and that's the vmap definition.
        adManifestPayload.isVmap = (sourceAds[0] as {type?: string}).type === 'vmap';
      }

      const currentAdBreak = this.player.ads?.currentAdBreak;
      if (isNotNullish(currentAdBreak)) {
        adManifestPayload.adBreakInfo = mapTheoAdBreak(currentAdBreak, playerDuration);
      }

      this.adCallbacks.onAdManifestLoaded(adManifestPayload);
    });

    this.player.addEventListener('playing', () => {
      // For SSAI breaks the play event reaches the SSAI pipeline through the regular
      // playback samples (manipulated by the SsaiService).
      if (this.currentBreakIsSsai) {
        return;
      }

      if (this.isLinearAdActive()) {
        this.adCallbacks.onPlay();
      }
    });

    this.player.addEventListener('pause', () => {
      // For SSAI breaks the pause event reaches the SSAI pipeline through the regular
      // playback samples (manipulated by the SsaiService).
      if (this.currentBreakIsSsai) {
        return;
      }

      if (this.isLinearAdActive()) {
        this.adCallbacks.onPause();
      }
    });
  }

  unload(): void {
    this.adCallbacks.onBeforeUnload();
  }
}
